import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../components/AuthContext";

/**
 * v68.17 — Fetch DocuSeal templates once for use in dropdowns.
 *
 * Cache is scoped per browser tab via a module-level ref so multiple mount
 * points don't re-fetch. Returns:
 *   { templates: Array<{id, name}>, loading: bool, error: string|null,
 *     refresh: () => Promise<void> }
 *
 * If DocuSeal is not configured (endpoint 400s/500s), the hook returns an
 * empty templates list and null error — callers should still allow raw ID
 * fallback so the UX works when only a template ID is known.
 */
let _cache = null;
let _cachePromise = null;

async function _fetch() {
  const { data } = await axios.get(`${API_BASE}/esign-docuseal/templates`);
  // Endpoint returns whatever DocuSeal responded with; normalise to array of
  // {id, name} so consumers don't need to know the raw shape.
  const raw = Array.isArray(data) ? data : (data?.data || data?.templates || []);
  return raw
    .map((t) => ({ id: t.id ?? t.template_id, name: t.name || t.title || `Template ${t.id}` }))
    .filter((t) => t.id != null);
}

export function useDocusealTemplates(enabled = true) {
  const [templates, setTemplates] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache && enabled);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    if (_cache) { setTemplates(_cache); setLoading(false); return; }
    let cancel = false;
    setLoading(true);
    (_cachePromise = _cachePromise || _fetch())
      .then((list) => {
        _cache = list;
        if (!cancel) { setTemplates(list); setError(null); }
      })
      .catch((e) => {
        _cachePromise = null;
        if (!cancel) setError(e?.response?.data?.detail || e.message || "DocuSeal offline");
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [enabled]);

  const refresh = async () => {
    _cache = null; _cachePromise = null;
    setLoading(true); setError(null);
    try {
      const list = await _fetch();
      _cache = list;
      setTemplates(list);
    } catch (e) { setError(e?.response?.data?.detail || e.message || "DocuSeal offline"); }
    finally { setLoading(false); }
  };

  return { templates, loading, error, refresh };
}
